import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import ProductDetailsModal from 'web-ui-kit/product-details/modal';
import { PDP_MODAL_Z_INDEX } from 'web-ui-kit/zIndexes';
import { FEATURE_FLAGS, getFeatureFlagEnabledBool } from 'shared/reducers/featureFlag';

import {
  PromoSelectors,
  UserFavoriteSelectors
} from 'services/Data';

import { getShoppingCart } from 'shared/components/Checkout/state';

import * as handlers from './handlers';
import * as reducers from './reducers';

/**
 * An adapter layer for rendering the new Monarch product details model.
 * Maps the existing data to the shape expected by shared PDM modal.
 * Maps the PDM events to the callbacks expected by existing implementation.
 */
function MonarchAdapter ({
  // DATA
  activeProduct,
  activeSpace,
  allLikeStatus,
  allRetailerPromos,
  alternativeProducts,
  alternativeProductsLoading,
  atcLoading,
  atcQuantity,
  fromCart,
  fromShop,
  initialProduct,
  isBuyFromRetailer,
  isFreeShipping,
  isMaterial,
  isPromoExcluded,
  isSellable,
  productAddOns,
  productAddOnsLoading,
  shoppingCart,
  spaceId,
  swapAllowed,
  swapInProductId,
  swapInProgress,
  uiMessaging,
  variantsByAttribute,
  variantsLoading,
  variantProducts,

  // FEATURE FLAGS
  freeShippingMessageEnabled,

  // CALLBACKS
  dispatch: reduxDispatcher,
  onAddMainProductToCart,
  onAddToCart,
  onBack,
  onBuyFromRetailer,
  onClose,
  onLikeDislikeProduct,
  onQuantityChange,
  onSelectAddonProduct,
  onSelectAltProduct,
  onSwapClick,
  onUpdateSelectedAttribute
}) {
  const [addToCartProductId, setAddToCartProductId] = useState(null);

  // Initialize the main event handler.
  const mainDispatcher = handleDispatch({
    // data
    activeProduct,
    activeSpace,
    alternativeProducts,
    initialProduct,
    isBuyFromRetailer,
    fromShop,
    spaceId,
    variantProducts,

    // callbacks
    onAddMainProductToCart,
    onAddToCart,
    onBack,
    onBuyFromRetailer,
    onClose,
    onLikeDislikeProduct,
    onQuantityChange,
    onSelectAddonProduct,
    onSelectAltProduct,
    onSwapClick,
    onUpdateSelectedAttribute,
    reduxDispatcher,
    setAddToCartProductId
  });

  // Reduce the product details state.
  const product = reducers.reduceProduct({
    product: activeProduct,
    allLikeStatus,
    allRetailerPromos
  });

  const atcStatus = reducers.reduceAtcStatus({
    atcLoading,
    activeProduct,
    isBuyFromRetailer,
    shoppingCart
  });

  const { variants, variantsState } = reducers.reduceVariants({
    activeProduct,
    initialProduct,
    allLikeStatus,
    allRetailerPromos,
    variantsByAttribute,
    variantProducts
  });

  const addons = reducers.reduceAddons({
    addToCartProductId,
    allLikeStatus,
    allRetailerPromos,
    productAddOns,
    shoppingCart
  });

  const similarProducts = reducers.reduceSimilarProducts({
    allLikeStatus,
    allRetailerPromos,
    alternativeProducts
  });

  const swapStatus = reducers.reduceSwapStatus({
    fromCart,
    product,
    swapAllowed,
    swapInProductId,
    swapInProgress
  });

  const productDetailsState = {
    addons,
    addonsLoading: productAddOnsLoading,
    atcQuantity,
    atcStatus,
    dislikeButtonConfig: fromShop ? 'never' : 'hover',
    isAltProduct: activeProduct !== initialProduct,
    isFreeShipping,
    isMaterial,
    isPromoExcluded,
    isSellable,
    loading: false,
    product,
    similarProducts,
    similarProductsLoading: alternativeProductsLoading,
    swapStatus,
    uiMessaging: uiMessaging,
    variants,
    variantsLoading,
    variantsState
  };

  return (
    <StyledProductDetailsModal
      dispatch={mainDispatcher}
      freeShippingMessageEnabled={freeShippingMessageEnabled}
      productDetailsState={productDetailsState}
    />
  );
}

const StyledProductDetailsModal = styled(ProductDetailsModal)`
  z-index: ${PDP_MODAL_Z_INDEX};
`;

/**
 * Translate PDM events into PDP callbacks and side effects.
 */
function handleDispatch ({
  // DATA
  activeProduct,
  activeSpace,
  alternativeProducts,
  initialProduct,
  isBuyFromRetailer,
  fromShop,
  spaceId,
  variantProducts,

  // CALLBACKS
  onAddMainProductToCart,
  onAddToCart,
  onBack,
  onBuyFromRetailer,
  onClose,
  onLikeDislikeProduct,
  onQuantityChange,
  onSelectAddonProduct,
  onSelectAltProduct,
  onSwapClick,
  onUpdateSelectedAttribute,
  reduxDispatcher,
  setAddToCartProductId
}) {
  return (event) => {
    switch (event.type) {
      case 'atc-button-clicked': {
        handlers.handleMainAddToCartClicked({
          event,
          isBuyFromRetailer,
          onAddMainProductToCart,
          onBuyFromRetailer
        });
        break;
      }

      case 'product-details-modal-close-request': {
        onClose();
        break;
      }

      case 'product-like-status-changed': {
        handlers.handleLikeDislikeChange({
          event,
          fromShop,
          onLikeDislikeProduct,
          product: activeProduct,
          reduxDispatcher,
          spaceId
        });
        break;
      }

      case 'pd-addlOptions-product-card-clicked': {
        handlers.handleAddlOptionsProductCardClicked({
          event,
          initialProduct,
          onSelectAltProduct,
          variantProducts
        });
        break;
      }

      case 'pd-similar-products-product-card-clicked': {
        handlers.handleSimilarProductsCardClicked({
          alternativeProducts,
          event,
          onSelectAltProduct
        });
        break;
      }

      case 'pd-addon-atc-button-clicked': {
        handlers.handleAddonProductAddToCartClicked({
          activeSpace,
          event,
          fromShop,
          onAddToCart,
          reduxDispatcher,
          setAddToCartProductId
        });
        break;
      }

      case 'pd-addon-card-clicked': {
        const { addon } = event.payload;
        onSelectAddonProduct(addon.whProduct);
        break;
      }

      case 'pd-back-nav-button-click': {
        onBack();
        break;
      }

      case 'pd-color-button-clicked': {
        handlers.handleVariantsColorClicked({
          event,
          onUpdateSelectedAttribute
        });
        break;
      }

      case 'pd-dims-button-clicked': {
        handlers.handleVariantsDimsClicked({
          event,
          onUpdateSelectedAttribute
        });
        break;
      }

      case 'quantity-dropdown-option-selected': {
        onQuantityChange(event.payload.option);
        break;
      }

      case 'swap-button-clicked': {
        handlers.handleSwapButtonClicked({
          event,
          onSwapClick
        });
        break;
      }

      default:
        break;
    }
  };
}

/**
 * Pull data from redux global state as needed.
 */
const mapStateToProps = (state, ownProps) => {
  const allLikeStatus = UserFavoriteSelectors.getAllLikeStatuses(state);
  const allRetailerPromos = PromoSelectors.getRetailerPromos(state);
  const shoppingCart = getShoppingCart(state);
  const freeShippingMessageEnabled = getFeatureFlagEnabledBool(FEATURE_FLAGS.ShowFreeShippingMessage, state);

  return {
    allRetailerPromos,
    allLikeStatus,
    freeShippingMessageEnabled,
    shoppingCart
  };
};

export default connect(mapStateToProps)(MonarchAdapter);
